import { useRouter }                              from "next/router";
import React, { forwardRef, useEffect, useState } from "react";

import { useGetProfileData }                                           from "_common/hooks/auth";
import { useGetAppLocale, useGetIsFollowStatus, useSetIsFollowStatus } from "_common/hooks/global";
import Toast                                                           from "_common/toast";
import { getBaseURL }                                                  from "_common/utils";
import { copyToClipboard }                                             from "_common/utils/clientSide";
import { useGetIsModerator }                                           from "_components/features/ivsChat/states";
import { useGetModerators, useSoftDeleteMod }                          from "_components/pages/studio/states";
import { useGetUpdateSession }                                         from "_jotai/liveSession";
import { deleteCommentAPI, muteUserFromSession, pinCommentSession }    from "_services/api/broadcast";
import { blockUserFromChat, getUserProfile }                           from "_services/api/user";

import Presentation from "./presentation";

const ChatBox = forwardRef ( ( props, ref ) => {
	const {
		emotesList,
		comments,
		toggleUpdatedComments,
		deleteComment,
		isTabsVisible,
		isSessionEnded,
		isStreamer,
		setActiveTab,
		sportsFanId: userId,
		streamerName,
		autoScrollEnabled,
		autoScrollToBottom,
		isBlockedByStreamer,
		isLive,
		togglePolls,
		handleModal,
		chatPoppedOut,
		onScroll
	} = props;

	const router             = useRouter ();
	const toggleFollowStatus = useSetIsFollowStatus ();
	const softDeleteMod      = useSoftDeleteMod ();
	const isModerator        = useGetIsModerator ();

	const [ popOverState, setBanPopUpState ]                  = useState ( null );
	const [ banOptionsPopUpState, setBanOptionsPopUpState ]   = useState ( null );
	const [ currentlySelectedUser, setCurrentlySelectedUser ] = useState ( null );
	const [ selectedComment, setSelectedComment ]             = useState ( null );
	const [ isPopUpOpen, setIsPopUpOpen ]                     = useState ( false );

	const [ followProfile, setFollowProfile ] = useState ( null );
	const isFollowingUser                     = useGetIsFollowStatus ();
	const appLocale                           = useGetAppLocale ();
	const userProfile                         = useGetProfileData ();
	const moderatorsList                      = useGetModerators ();
	const sessionData                         = useGetUpdateSession ();
	const broadcaster                         = sessionData?.broadcaster || {};
	const sessionId                           = sessionData?.id;
	const isStreamerComment                   = broadcaster?.sportsFan?.id === selectedComment?.sportsFan?.id;
	const streamerUserId                      = sessionData?.broadcaster?.sportsFan?.id;

	useEffect ( () => {
		if ( !userId )
			return;

		getFollowStatus ();

	}, [ userId ] );

	const getFollowStatus = async () => {

		const userProfileHeaders = { userId };
		let userProfileParams    = { follow: true, isFollowing: true };

		const userProfile = await getUserProfile ( userProfileHeaders, userProfileParams );

		setFollowProfile ( userProfile?.isFollowing?.following );
		toggleFollowStatus ( userProfile?.isFollowing?.following );
	};

	const handleDropDownPopup = ( event, comment ) => {
		setIsPopUpOpen ( true );
		toggleUpdatedComments ( false );
		setSelectedComment ( comment );
		setCurrentlySelectedUser ( comment.sportsFan );
		setBanPopUpState ( event.currentTarget );
	};

	const handleCloseDropDownPopup = () => {
		setIsPopUpOpen ( false );
		setBanPopUpState ( null );
		setBanOptionsPopUpState ( null );
		setCurrentlySelectedUser ( null );
		toggleUpdatedComments ( true );
		autoScrollToBottom ();
	};

	const openDropDownPopup = () => {
		setBanOptionsPopUpState ( popOverState );
		setBanPopUpState ( null );
	};

	const muteUser = async time => {

		try {
			await muteUserFromSession ( { userId: currentlySelectedUser.id, time, streamerId: streamerUserId, isModerator } );

			Toast.info ( "Successfully muted user from commenting." );
		} catch ( error ) {
			Toast.error ( error.displayMsg );
		}

		handleCloseDropDownPopup ();
	};

	const blockUser = async () => {
		const { broadcastSessionId } = selectedComment;

		try {

			await blockUserFromChat ( { userId: selectedComment.sportsFan.id, broadcastSessionId, streamerUserId } );
			Toast.info ( "User blocked successfully." );

			const isUserExistsInModList = moderatorsList.find ( mod => mod.id === currentlySelectedUser.id );

			if ( isUserExistsInModList ) {
				softDeleteMod ( { id: selectedComment.sportsFan.id } );
			}

		} catch ( error ) {
			Toast.error ( error.displayMsg );
		}

		handleCloseDropDownPopup ();
	};
	// deleteComment ( selectedComment );
	const deleteCommentHandler = async () => {
		try {
			await deleteCommentAPI ( { broadcastSessionId: selectedComment.broadcastSessionId, commentId: selectedComment.id } );
			deleteComment ( selectedComment );
			Toast.info ( "Comment deleted successfully." );

		} catch ( err ) {
			Toast.error ( err.displayMsg );
		}

		handleCloseDropDownPopup ();
	};

	const pinComment = async () => {
		const { broadcastSessionId, id, createdAt } = selectedComment;

		if ( !createdAt ) {
			handleCloseDropDownPopup ();

			return Toast.error ( "Processing comment, please wait..." );

		}
		try {
			await pinCommentSession ( {
				broadcastSessionId : broadcastSessionId,
				commentId          : id,
				flag               : 1
			} );

			Toast.info ( "Comment Pinned" );
		} catch ( err ) {
			Toast.error ( err.displayMsg );
		}

		handleCloseDropDownPopup ();
	};

	const getSrc = () => {
		if ( !isLive )
			return;

		let shareLink = `${ getBaseURL () }/stream/${ sessionId }`;

		copyToClipboard ( shareLink );
	};

	const handleActiveTab = () => {
		setActiveTab ( "FAN_RANK_LEADERBOARD" );
	};

	const handleChatTips = type => {
		switch ( type ) {
			case "WARNING": return;

			case "DONATE":
				// already handled separately so return
				return;

			case "FAN_RANK": {
				// show fanrank leaderboard
				return handleActiveTab;
			}

			case "FOLLOW": {
				// already handled separately so return
				return;
			}

			case "SHARE": {
				// handle share
				return getSrc;
			}

			case "POLL": {
				return togglePolls;
			}

			default:
				return;
		}
	};

	return (
		<Presentation
			ref = { ref }
			appLocale = { appLocale }
			autoScrollEnabled = { autoScrollEnabled }
			banOptionsPopUpState = { banOptionsPopUpState }
			blockUser = { blockUser }
			chatPoppedOut = { chatPoppedOut }
			comments = { comments }
			currentlySelectedUser = { currentlySelectedUser }
			deleteComment = { deleteCommentHandler }
			emotesList = { emotesList }
			handleChatTips = { handleChatTips }
			handleCloseDropDownPopup = { handleCloseDropDownPopup }
			handleDropDownPopup = { handleDropDownPopup }
			handleModal = { handleModal }
			isBlockedByStreamer = { isBlockedByStreamer }
			isFollowingUser = { isFollowingUser ? isFollowingUser : followProfile }
			isLive = { isLive }
			isModerator = { isModerator }
			isPopUpOpen = { isPopUpOpen }
			isSessionEnded = { isSessionEnded }
			isStreamer = { isStreamer }
			isStreamerComment = { isStreamerComment }
			isTabsVisible = { isTabsVisible }
			muteUser = { muteUser }
			onScroll = { onScroll }
			openDropDownPopup = { openDropDownPopup }
			pinComment = { pinComment }
			popOverState = { popOverState }
			router = { router }
			selectedComment = { selectedComment }
			streamerName = { streamerName }
			togglePolls = { togglePolls }
			userId = { userId }
			userProfile = { userProfile }
		/>
	);
} );

ChatBox.displayName = "component";
export default ChatBox;
